import { type Slide } from './_data';
import html2pdf from 'html2pdf.js';
import { jsPDF } from 'jspdf';
import axios from 'axios';
import { settings } from 'config';
//import * as PDFLib from 'pdf-lib';

export const getSlideField = (
  slide: Slide<any>,
  field: string,
): Record<string, any> | undefined => {
  if (!slide?.content || typeof field !== 'string') {
    return undefined;
  }
  return Object.freeze({ ...slide.content.find(item => item.field === field) });
};

export const getSlidePageNumber = (
  slides: Slide<any>[],
  slide: Slide<any>,
): number => {
  const index = slides.findIndex(
    s => s.slideTemplateId === slide.slideTemplateId,
  );
  return index >= 0 ? index + 1 : 0;
};

const COVER_SLIDE_IDS = new Set([0, 10]);

export const isCoverSlide = (slide: Slide<any>): boolean =>
  COVER_SLIDE_IDS.has(slide.id);

export const createGoogleApiRequests = (slides: Slide<any>[]): any[] => {
  if (!Array.isArray(slides)) {
    return [];
  }

  return slides.flatMap(slide => {
    if (!slide?.content || !slide.slideTemplateId) {
      return [];
    }

    const editableFields = slide.content.filter(content => {
      if (!content?.type || !content.field) return false;
      return (
        content.type === 'editableText' || content.type === 'editableTextarea'
      );
    });

    return editableFields.map(editableField => ({
      replaceAllText: Object.freeze({
        pageObjectIds: [slide.slideTemplateId],
        containsText: {
          text: `{{${editableField.field}}}`,
          matchCase: true,
        },
        replaceText: String(editableField.value ?? ''),
      }),
    }));
  });
};

export const downloadPdfSlides = async (
  slides: HTMLElement[],
  filename: string,
  width: number,
  height: number,
): Promise<void> => {
  const orientation = width > height ? 'landscape' : 'portrait';
  // Crear una instancia de jsPDF con valores explícitos
  const doc = new jsPDF(orientation, 'px', [width, height]);

  // Configuración para html2canvas
  const html2canvasOptions = {
    scale: 2,
    useCORS: true,
    letterRendering: true,
    allowTaint: false,
    logging: false,
    width: width,
    height: height,
  };

  // Procesar cada slide individualmente
  for (let i = 0; i < slides.length; i++) {
    const slide = slides[i];

    // Clonar el slide actual para manipularlo sin afectar el original
    const slideClone = slide.cloneNode(true) as HTMLElement;
    slideClone.style.width = `${width}px`;
    slideClone.style.height = `${height}px`;

    // Crear un contenedor temporal para el slide
    const container = document.createElement('div');
    container.style.width = `${width}px !important`;
    container.style.height = `${height}px !important`;
    container.style.maxWidth = `${width}px !important`;
    container.style.maxHeight = `${height}px !important`;
    container.appendChild(slideClone);

    try {
      // Usar html2pdf para obtener la imagen del slide
      const pageImage = await html2pdf()
        .from(container)
        .set({
          html2canvas: html2canvasOptions,
        })
        .outputImg();

      // Añadir una nueva página para todos los slides excepto el primero
      if (i !== 0) {
        doc.addPage([width, height], orientation);
      }

      // Añadir la imagen al documento PDF
      doc.addImage(
        pageImage.src,
        'JPEG',
        0,
        0,
        pageImage.width / 2,
        pageImage.height / 2,
      );
    } catch (error) {
      console.error(`Error processing slide ${i}:`, error);
    } finally {
      // Limpiar - eliminar el contenedor temporal
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  }

  // Guardar el PDF
  doc.save(`${filename}.pdf`);
};

// Helper function to convert image URL to base64
const convertImageUrlToBase64 = async (imageUrl: string): Promise<string> => {
  try {
    // If the URL is relative (starts with /), make it absolute
    const fullUrl = imageUrl.startsWith('/')
      ? `${window.location.origin}${imageUrl}`
      : imageUrl;

    const response = await fetch(fullUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`Error fetching image from ${imageUrl}:`, error);
    throw error;
  }
};

// Enhanced function to process slides with better image handling
const processSlidesWithImages = async (
  slides: HTMLElement[],
): Promise<string[]> => {
  const processedSlides = await Promise.all(
    slides.map(async slide => {
      // Clone the slide to avoid modifying the original
      const slideClone = slide.cloneNode(true) as HTMLElement;

      // Find all images in the slide
      const images = slideClone.querySelectorAll('img');

      // Process each image
      await Promise.all(
        Array.from(images).map(async img => {
          const src = img.getAttribute('src');
          const isBase64 = src?.startsWith('data:image');

          if (src && !isBase64) {
            try {
              const base64Src = await convertImageUrlToBase64(src);
              img.setAttribute('src', base64Src);
            } catch (error) {
              console.error(`Error converting image ${src} to base64:`, error);
            }
          }
        }),
      );

      return slideClone.outerHTML;
    }),
  );

  return processedSlides;
};

/**
 * Used for the new system of report generation with Puppeteer in user-ms
 * @param slides - The slides to download
 * @param styles - The styles for the slides
 * @param filename - The name of the PDF file
 * @param width - The width of the PDF
 * @param height - The height of the PDF
 */
export const downloadPdfSlidesRemote = async (
  slides: HTMLElement[],
  styles: string,
  filename: string,
  width: number,
  height: number,
): Promise<void> => {
  try {
    const processedSlidesHtml = await processSlidesWithImages(slides);

    const response = await axios.post(
      `${settings.api.baseUrl}/v2/generatePDF`,
      {
        slidesHtml: processedSlidesHtml,
        styles,
        width,
        height,
        filename,
      },
      {
        headers: {
          'X-Tifin-Ai-Auth': localStorage.getItem('x-tifin-ai-token'),
        },
        responseType: 'blob',
      },
    );

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}.pdf`);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error downloading PDF:', err);
  }
};

/*const unirPDFs = async (urls: string[], nombreFinal: string): Promise<void> => {
  const pdfFinal = await PDFLib.PDFDocument.create();

  for (const url of urls) {
    const pdfBytes = await fetch(url).then(res => res.arrayBuffer());
    const pdf = await PDFLib.PDFDocument.load(pdfBytes, {
      ignoreEncryption: true,
    });
    const paginas = await pdfFinal.copyPages(pdf, pdf.getPageIndices());
    paginas.forEach(pagina => pdfFinal.addPage(pagina));
  }

  const pdfBytesFinal = await pdfFinal.save();
  const blob = new Blob([pdfBytesFinal], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = nombreFinal;
  link.click();
  URL.revokeObjectURL(link.href);
};
*/
